import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useMemo} from 'react';

import {AntibodyFormat, DistinctDomain} from 'antibodies/AntibodyTypedefs';
import {distinctDomainToColor, formatToSortedDistinctDomains} from 'antibodies/AntibodyUtils';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FieldTableHeader from 'registry/field/FieldTableHeader';
import SelectSearchableDropdown from 'ui-library/dropdowns/SelectSearchableDropdown';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {OptionData} from 'ui-library/menus/MenuConstants';

import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';

export const ANY_AA_SEQUENCE_DISPLAY = 'Any AA sequence';

function ProteinSchemaOption({iconId, label}: {iconId: BenchlingIconName; label: string}): JSX.Element {
  return (
    <div>
      <BenchlingIcon className={css(styles.icon)} icon={iconId} />
      <span>{label}</span>
    </div>
  );
}

/**
 * Renders the domain type and specificity, with a swatch corresponding to the color for that
 * (domain, specificity) pair in the antibody viz.
 * Exported for testing.
 * @package
 */
export function DomainTypeAndSpecificityCell({
  distinctDomain,
}: {
  distinctDomain: DistinctDomain;
}): JSX.Element {
  const {type, specificity} = distinctDomain;
  return (
    <div className={classNames('simpleDivTable-cell', css(styles.tableCell))}>
      <div className={css(styles.swatch)} style={{backgroundColor: distinctDomainToColor(distinctDomain)}} />
      <span>
        {type}
        {specificity && <> ({specificity})</>}
      </span>
    </div>
  );
}

export interface AntibodyConfigurationTableProps {
  /* The antibody format for which an antibody schema is being configured */
  format: AntibodyFormat;
  /* All tag schemas that currently exist on the registry */
  allSchemas: Array<TagSchemaSummaryGraphQL>;
}

export default function AntibodyConfigurationTable({
  format,
  allSchemas,
}: AntibodyConfigurationTableProps): JSX.Element {
  const sortedDistinctDomains = useMemo(() => formatToSortedDistinctDomains(format), [format]);

  // @ts-expect-error TS does not believe that TagSchemaSupportedFolderItemType overlaps with
  // ModelTypeConstants despite the former being comprised entirely of members of the latter
  const proteinSchemas = allSchemas.filter((schema) => schema.folderItemType === ModelTypeConstants.PROTEIN);
  const proteinSchemasById = proteinSchemas.reduce<Record<string, TagSchemaSummaryGraphQL>>(
    (acc, proteinSchema) => {
      acc[proteinSchema.id] = proteinSchema;
      return acc;
    },
    {}
  );
  const optionDatas: Array<OptionData> = [
    {icon: 'protein', name: ANY_AA_SEQUENCE_DISPLAY, value: ModelTypeConstants.PROTEIN},
    ...proteinSchemas.map(({iconId, id, name}) => ({
      icon: (iconId ?? 'protein') as BenchlingIconName,
      name,
      value: id,
    })),
  ];

  return (
    <div className={classNames('simpleDivTable', css(styles.table))}>
      <FieldTableHeader
        isDisabled={true}
        onAddField={null}
        subheader={<>Definition</>}
        title="Format domain field"
      />
      {sortedDistinctDomains.map((distinctDomain) => {
        const {type, specificity} = distinctDomain;
        const key = `${type}${specificity != null ? `-${specificity}` : ''}`;
        return (
          <div key={key} className={classNames('simpleDivTable-row', css(styles.tableRow))}>
            <DomainTypeAndSpecificityCell distinctDomain={distinctDomain} />
            <div className="simpleDivTable-cell">
              <SelectSearchableDropdown
                // Always disable for now.
                disabled={true}
                menuProps={{isMulti: false}}
                noneSelectedMessage={<ProteinSchemaOption iconId="protein" label="Any AA Sequence" />}
                onSelect={noop}
                optionDatas={optionDatas}
                renderSelectedValues={([schemaId]) => {
                  const proteinSchema = proteinSchemasById[schemaId] ?? {
                    iconId: 'protein',
                    name: 'Any AA Sequence',
                  };
                  return (
                    <ProteinSchemaOption
                      iconId={(proteinSchema.iconId ?? 'protein') as BenchlingIconName}
                      label={proteinSchema.name}
                    />
                  );
                }}
                selectedValues={[ModelTypeConstants.PROTEIN]}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

const styles = StyleSheet.create({
  table: {
    marginTop: '24px',
  },
  tableRow: {
    backgroundColor: Color.WHITE,
  },
  tableCell: {
    display: 'flex',
  },
  swatch: {
    width: '4px',
    minWidth: '4px',
    marginRight: '4px',
    flex: '0 0 4px',
  },
  icon: {
    marginRight: '4px',
  },
});
